import {showNotification} from 'platform/components';
import {match} from 'ts-pattern';
import {z} from 'zod';

import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {type ApiError} from 'shared/web';

import {getUnavailableMessage} from './getUnavailableMessage';

const t = (key: string, fallback: string) => i18n.t(key, {defaultValue: fallback});

// RTK Query rejects with a FetchBaseQueryError or a SerializedError; both are plain objects.
const isApiError = (error: unknown): error is ApiError =>
  typeof error === 'object' && error !== null;

const getStatus = (error: unknown) =>
  typeof error === 'object' && error !== null && 'status' in error ? error.status : null;

const ErrorSchema = z.object({
  data: z.object({
    errors: z.array(z.object({code: z.string(), message: z.string().optional()})).min(1),
  }),
});

const getFirstError = (error: unknown) => ErrorSchema.safeParse(error).data?.data.errors[0];

const getErrorCode = (error: unknown): string | null => getFirstError(error)?.code ?? null;

const UNAVAILABLE_REASONS = [
  'EXPIRED',
  'PRICE_EXPIRED',
  'PRICE_MISSING',
  'SOURCE_CHANGED',
  'SOURCE_UNAVAILABLE',
  'OFFERED_ELSEWHERE',
] as const;

// DMS-BC-OA-0003 carries its reason only inside the (translated) message, as the enum token.
const getUnavailableReason = (error: unknown) => {
  const tokens = (getFirstError(error)?.message ?? '').split(/\W+/);

  return UNAVAILABLE_REASONS.find((reason) => tokens.includes(reason)) ?? null;
};

const getOfferedElsewhereMessage = () =>
  t(
    'entity.offerAlternative.source.offeredElsewhere',
    'This vehicle is already offered in another business case. Choose another vehicle.'
  );

/**
 * The legacy deal-vehicle selection (add, switch, create with a vehicle) is refused with
 * DMS-BC-OA-0005 when another open Business Case offers the vehicle as an alternative. Other
 * failures keep the legacy handling.
 */
export const handleSaleVehicleSelectionError = (error: ApiError) => {
  if (getErrorCode(error) === 'DMS-BC-OA-0005') {
    showNotification.warning(getOfferedElsewhereMessage());
    return;
  }
  handleApiError(error);
};

/**
 * Known offer-alternative failures get a specific message; the mutations invalidate the list on
 * error too, so a stale or vanished alternative is refetched without extra work here.
 */
export const isStaleRevisionError = (error: unknown) =>
  getErrorCode(error) === 'DMS-BC-OA-0002' || getStatus(error) === 409;

export const isCurrencyMismatchError = (error: unknown) => getErrorCode(error) === 'DMS-BC-OA-0006';

export const handleOfferAlternativeError = (
  error: unknown,
  // Pass `blockerContext?.reportApiFailure` so a save-and-leave keeps the unsaved edits.
  options: {signalApiError?: VoidFunction} = {}
) => {
  if (!isApiError(error)) {
    showNotification.error();
    return;
  }
  options.signalApiError?.();

  match<{status: unknown; code: string | null}>({
    status: getStatus(error),
    code: getErrorCode(error),
  })
    .with({code: 'DMS-BC-OA-0001'}, () =>
      showNotification.warning(
        t(
          'entity.offerAlternative.notifications.notFound',
          'This alternative no longer exists. The list was refreshed.'
        )
      )
    )
    .with({code: 'DMS-BC-OA-0003'}, () =>
      showNotification.warning(
        getUnavailableMessage(getUnavailableReason(error)) ??
          t(
            'entity.offerAlternative.notifications.notSelectable',
            'This vehicle or alternative is not available right now. Check that the vehicle is for sale, priced and not reserved, and that the offer is still valid.'
          )
      )
    )
    .with({code: 'DMS-BC-OA-0005'}, () => showNotification.warning(getOfferedElsewhereMessage()))
    .with({code: 'DMS-BC-OA-0006'}, () =>
      showNotification.warning(
        t(
          'entity.offerAlternative.notifications.currencyMismatch',
          'All alternatives of an offer must be in the same currency.'
        )
      )
    )
    // The Business Case moved past the offer meanwhile (e.g. a contract was closed).
    .with({code: 'DMS-BC-OA-0007'}, () =>
      showNotification.warning(
        t(
          'entity.offerAlternative.notifications.inProcessing',
          'The offer can no longer be changed because the business case has moved on.'
        )
      )
    )
    .with({code: 'DMS-BC-OA-0008'}, () =>
      showNotification.warning(
        t(
          'entity.offerAlternative.notifications.appliedAlternative',
          'This alternative is the deal vehicle now. Change it in the deal instead.'
        )
      )
    )
    .with({code: 'DMS-BC-OA-0009'}, () =>
      showNotification.warning(
        t(
          'entity.offerAlternative.notifications.invalidTerms',
          'These terms are not valid for this alternative. Check the discount and upsell prices.'
        )
      )
    )
    .with({code: 'DMS-BC-OA-0012'}, () =>
      showNotification.warning(
        t(
          'entity.offerAlternative.notifications.alreadyInOffer',
          'This vehicle or configuration is already an alternative in this offer.'
        )
      )
    )
    .with({code: 'DMS-BC-OA-0011'}, () =>
      showNotification.warning(
        t(
          'entity.offerAlternative.notifications.dealVehicleWithoutPrice',
          'Set a selling price for the sale vehicle before adding alternatives.'
        )
      )
    )
    // Stale revision; the bare status stays as a fallback for a response without the code.
    .with({code: 'DMS-BC-OA-0002'}, {status: 409}, () =>
      showNotification.warning(
        t(
          'entity.offerAlternative.notifications.revisionConflict',
          'This alternative was changed meanwhile. Review it and try again.'
        )
      )
    )
    .otherwise(() => handleApiError(error));
};
